import { Link, useNavigate } from "@tanstack/react-router";
import { Mail, Lock, Eye, EyeOff, UserPlus, User } from "lucide-react";
import { useState } from "react";
import { toast } from "@/shared/components";
import { supabase } from "@/shared/lib/supabase";
import { Input, Label, Checkbox } from "@/shared/components";
import { AuthTemplate } from "@modootoday/ui-widgets/templates";

export function SignupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    passwordConfirm: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [agree, setAgree] = useState({
    terms: false,
    privacy: false,
    marketing: false,
  });
  const [loading, setLoading] = useState(false);

  const set =
    (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((p) => ({ ...p, [key]: e.target.value }));

  const passwordMatch =
    form.password &&
    form.passwordConfirm &&
    form.password === form.passwordConfirm;
  const passwordMismatch =
    form.passwordConfirm && form.password !== form.passwordConfirm;
  const canSubmit =
    form.name &&
    form.email &&
    form.password &&
    passwordMatch &&
    agree.terms &&
    agree.privacy;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);

    try {
      const { error } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: {
          data: {
            full_name: form.name,
            marketing_agree: agree.marketing,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;

      toast.success("가입 신청이 완료되었습니다. 이메일을 확인해 주세요.");
      navigate({
        to: "/auth/verify-email",
        search: { email: form.email },
      });
    } catch (err: any) {
      toast.error(err.message || "회원가입에 실패했습니다");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthTemplate
      title="회원가입"
      subtitle="무료로 시작하세요 — 카드 등록 불필요"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label className="mb-1.5 block">이름 (닉네임)</Label>
          <Input
            type="text"
            placeholder="홍길동"
            value={form.name}
            onChange={set("name")}
            leadingIcon={<User className="size-4" />}
          />
        </div>

        <div>
          <Label className="mb-1.5 block">이메일</Label>
          <Input
            type="email"
            placeholder="you@example.com"
            value={form.email}
            onChange={set("email")}
            leadingIcon={<Mail className="size-4" />}
          />
        </div>

        <div>
          <Label className="mb-1.5 block">비밀번호</Label>
          <Input
            type={showPw ? "text" : "password"}
            placeholder="8자 이상"
            value={form.password}
            onChange={set("password")}
            leadingIcon={<Lock className="size-4" />}
            trailingIcon={
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                aria-label={showPw ? "비밀번호 숨기기" : "비밀번호 표시"}
                className="text-text-tertiary hover:text-text-primary"
              >
                {showPw ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </button>
            }
          />
        </div>

        <div>
          <Label className="mb-1.5 block">비밀번호 확인</Label>
          <Input
            type={showPw ? "text" : "password"}
            placeholder="비밀번호를 다시 입력하세요"
            value={form.passwordConfirm}
            onChange={set("passwordConfirm")}
            leadingIcon={<Lock className="size-4" />}
            className={passwordMismatch ? "border-signal-danger" : ""}
          />
          {passwordMismatch && (
            <p className="mt-1 text-[11px] text-signal-danger">
              비밀번호가 일치하지 않습니다
            </p>
          )}
        </div>

        <div className="space-y-2 rounded-lg bg-surface-sunken/50 p-3">
          <label className="flex items-center gap-2 text-[12px]">
            <Checkbox
              checked={agree.terms}
              onCheckedChange={(checked: boolean) =>
                setAgree((p) => ({ ...p, terms: !!checked }))
              }
            />
            <span className="text-text-tertiary">
              <a
                href="/docs/terms"
                className="font-semibold text-text-primary underline-offset-2 hover:underline"
              >
                이용약관
              </a>
              에 동의합니다 <span className="text-signal-danger">*</span>
            </span>
          </label>
          <label className="flex items-center gap-2 text-[12px]">
            <Checkbox
              checked={agree.privacy}
              onCheckedChange={(checked: boolean) =>
                setAgree((p) => ({ ...p, privacy: !!checked }))
              }
            />
            <span className="text-text-tertiary">
              <a
                href="/docs/privacy"
                className="font-semibold text-text-primary underline-offset-2 hover:underline"
              >
                개인정보 처리방침
              </a>
              에 동의합니다 <span className="text-signal-danger">*</span>
            </span>
          </label>
          <label className="flex items-center gap-2 text-[12px]">
            <Checkbox
              checked={agree.marketing}
              onCheckedChange={(checked: boolean) =>
                setAgree((p) => ({ ...p, marketing: !!checked }))
              }
            />
            <span className="text-text-tertiary">
              마케팅 정보 수신에 동의합니다 (선택)
            </span>
          </label>
        </div>

        <button
          type="submit"
          disabled={!canSubmit || loading}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-accent py-2.5 text-[13px] font-semibold text-text-inverse transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {loading ? (
            <span className="size-4 animate-spin rounded-full border-2 border-text-inverse/30 border-t-primary-foreground" />
          ) : (
            <UserPlus className="size-4" />
          )}
          {loading ? "가입 처리 중..." : "무료로 시작하기"}
        </button>
      </form>

      <p className="mt-6 text-center text-[12px] text-text-tertiary">
        이미 계정이 있으신가요?{" "}
        <Link
          to="/auth/login"
          className="font-semibold text-text-brand hover:underline"
        >
          로그인
        </Link>
      </p>
    </AuthTemplate>
  );
}
